import { Box, Divider, List, Paragraph, UnstyledButton } from '@zillow/constellation-10';
import getLatestMessageDateText from '../../utils/getLatestMessageDateText';
import { FULLSTORY_EXCLUDE_CLASS_NAME } from '../../constants';
import MessageContent from './MessageContent';
import UnreadIndicator from '../UnreadIndicator';
import { conversationItemStyles, listItemStyles, messageContentWrapperStyles } from './styles';

export type ConversationItemProps = {
  title: string;
  subtitle: string;
  latestMessage: string;
  latestMessageDateMs: string;
  isMessageOwner: boolean;
  isUnread: boolean;
  isPhoneLead: boolean;
  isArchived?: boolean;
  isSpamOrReported?: boolean;
  footer?: React.ReactNode;
  thumbnail?: React.ReactNode;
  isSelected: boolean;
  onClick: () => void;
  dataEventId?: string;
};

export default function ConversationItem({
  title,
  subtitle,
  latestMessage,
  latestMessageDateMs,
  isMessageOwner,
  isUnread,
  isPhoneLead,
  isArchived = false,
  isSpamOrReported = false,
  footer,
  thumbnail,
  isSelected,
  onClick,
  dataEventId,
}: ConversationItemProps) {
  return (
    <List.Item data-testid="conversation-item" data-eventid={dataEventId} css={listItemStyles}>
      <Box
        asChild
        css={{
          ...conversationItemStyles,
          ...(isSelected && {
            // TODO: RRMP-5329: Replace with 'bg.action.neutral.selected.default' when legacy theme is removed
            // Hardcoded during legacy theme migration - legacy maps this token to blue; using gray for consistency
            backgroundColor: '#eeeef0',
            borderLeft: '4px solid',
            borderLeftColor: 'border.express.trust.hero',
            _hover: {
              // TODO: RRMP-5329: Replace with 'bg.action.neutral.selected.hover' when legacy theme is removed
              backgroundColor: '#dddde1',
            },
          }),
        }}
        onClick={onClick}
        aria-current={isSelected ? 'true' : undefined}
        aria-pressed={isSelected}
      >
        <UnstyledButton>
          {thumbnail && <Box css={{ marginRight: 'tighter', flexShrink: 0, position: 'relative' }}>{thumbnail}</Box>}

          <Box css={{ flex: 1, minWidth: 0, paddingLeft: thumbnail ? 'tight' : 0 }}>
            {/* Title and Date */}
            <Box
              css={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Paragraph
                textStyle={isUnread ? 'body-bold' : 'body'}
                css={{
                  flex: 1,
                  marginRight: 'tight',
                  truncate: 'end',
                }}
                className={FULLSTORY_EXCLUDE_CLASS_NAME}
                data-testid="participant-name"
              >
                {title}
              </Paragraph>

              <Paragraph textStyle="body-xs" css={{ color: 'text.subtle', flexShrink: 0 }}>
                {getLatestMessageDateText(latestMessageDateMs)}
              </Paragraph>
            </Box>

            {/* Subtitle */}
            {subtitle && (
              <Paragraph
                textStyle="body-xs"
                css={{
                  color: 'text.subtle',
                  truncate: 'end',
                }}
                className={FULLSTORY_EXCLUDE_CLASS_NAME}
              >
                {subtitle}
              </Paragraph>
            )}

            {/* Message & Unread indicator */}
            <Box data-testid="message-preview" css={messageContentWrapperStyles}>
              <MessageContent
                isPhoneLead={isPhoneLead}
                isMessageOwner={isMessageOwner}
                latestMessage={latestMessage}
                isArchived={isArchived}
                isSpamOrReported={isSpamOrReported}
                isUnread={isUnread}
              />

              {isUnread && <UnreadIndicator dataTestId="unread-badge" />}
            </Box>

            {/* Footer components */}
            {footer && (
              <Box css={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 'tight' }}>
                {footer}
              </Box>
            )}
          </Box>
        </UnstyledButton>
      </Box>

      {/* Divider is rendered outside the button for better semantics */}
      <Divider />
    </List.Item>
  );
}
